import React, { useCallback, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import { ContentState, EditorState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import S from '../S';
import styles from './EditorTextArea.less';
import { ToolbarConfig } from './EditorTextArea.types';
import { GetDefaultEditorConfig, convertMarkup } from './EditorTextArea.utils';

type EditorTextAreaProps = {
  onChange?: (ev: unknown) => void;
  toolbar?: ToolbarConfig;
  spellCheck?: boolean;
  value?: string;
  defaultValue?: string;
  charCounter?: boolean;
  charCounterBelow?: boolean;
  maxLength?: number;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  noBorder?: boolean;
  className?: string;
};

const EditorTextArea: React.FC<EditorTextAreaProps> = ({
  onChange,
  toolbar,
  spellCheck,
  value: controlledValue,
  defaultValue,
  charCounter,
  charCounterBelow,
  maxLength,
  disabled,
  placeholder,
  id,
  noBorder,
  className = '',
}) => {
  const [value, setValue] = useState<string | undefined>(defaultValue);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [initialized, setInitialized] = useState(false);

  const isControlled = useMemo(
    () => controlledValue != null,
    [controlledValue],
  );

  const getValue = useCallback(
    () => (isControlled ? controlledValue : value),
    [isControlled, controlledValue, value],
  );

  const updateContentState = useCallback((html = '') => {
    const contentBlock = htmlToDraft(html);
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(
        contentBlock.contentBlocks,
      );
      const content = EditorState.createWithContent(contentState);
      setEditorState(content);
    }
  }, []);

  useEffect(() => {
    if (defaultValue) {
      updateContentState(getValue());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialized && controlledValue) {
      updateContentState(controlledValue);
      setInitialized(true);
    }
  }, [controlledValue, initialized, updateContentState]);

  const handleChange = (state: EditorState) => {
    const markup = convertMarkup(
      draftToHtml(convertToRaw(state.getCurrentContent())),
    );
    if (!isControlled) {
      setValue(markup);
    }

    setEditorState(state);
    if (onChange) {
      onChange(markup);
    }
  };

  const currentValue = getValue();
  const charCount = (currentValue ?? '').length;

  const textAreaNode = (
    <div
      className={cn(styles.editor, {
        [styles.disabled]: disabled,
        [styles.noBorder]: noBorder,
        [className]: className,
      })}
    >
      <Editor
        spellCheck={spellCheck}
        editorState={editorState}
        onEditorStateChange={handleChange}
        stripPastedStyles
        readOnly={disabled}
        toolbarClassName={disabled ? styles.toolbarClassName : undefined}
        toolbar={GetDefaultEditorConfig(toolbar)}
      />
    </div>
  );

  if (charCounter) {
    return (
      <div className={styles.container}>
        <S size="5" className={styles.charCount}>
          {charCount}/{maxLength}
        </S>
        {textAreaNode}
      </div>
    );
  }

  if (charCounterBelow) {
    return (
      <div className={styles.container}>
        <label htmlFor={id}>{placeholder}</label>
        {textAreaNode}
        <S size="5" className={styles.charCountBelow}>
          {charCount}/{maxLength}
        </S>
      </div>
    );
  }

  return textAreaNode;
};

export default EditorTextArea;
